/**
 * Splits a non-negative finite duration into `{ totalSeconds, minutes, seconds }`,
 * where `seconds` is the remainder after whole minutes (decimal preserved).
 * Returns `undefined` for non-finite or negative input — callers render a zero
 * placeholder in those cases.
 */
export function parseElapsed(
  ms: number,
): { totalSeconds: number; minutes: number; seconds: number } | undefined {
  if (!Number.isFinite(ms) || ms < 0) return undefined;
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;
  return { totalSeconds, minutes, seconds };
}

export function formatElapsed(ms: number): string {
  const parsed = parseElapsed(ms);
  if (!parsed) return '0s';
  const { totalSeconds, minutes, seconds } = parsed;
  if (totalSeconds < 60) {
    return `${totalSeconds.toFixed(1).replace(/\.0$/, '')}s`;
  }
  return `${minutes}m ${Math.floor(seconds)}s`;
}

export function toIsoDuration(ms: number): string {
  const parsed = parseElapsed(ms);
  if (!parsed) return 'PT0S';
  const { totalSeconds, minutes, seconds } = parsed;
  if (totalSeconds < 60) {
    return `PT${totalSeconds.toFixed(3).replace(/\.?0+$/, '')}S`;
  }
  const secondsStr = seconds.toFixed(3).replace(/\.?0+$/, '');
  return `PT${minutes}M${secondsStr}S`;
}
